import React, { useEffect, useState, useRef } from 'react';
import { useParams } from 'react-router';

// API/Components
import {
	ANALYSIS_RESULT,
	DATA_ERROR_VALUE,
	GRAPH_BOUNDS,
	GRAPH_STYLE,
	STATE_COLOR,
	isEmpty,
	validValue,
	AnalysisResultToStateColor,
	formatDateTime,
	measUnitsConversion,
	getUserTempUnits
} from './enums';
import { DrawHorizontalDashedLine, DrawVerticalDashedLineAtTooltip } from './chart_helpers';

// Material UI
import Button from '@mui/material/Button';
import CenterFocusStrongIcon from '@mui/icons-material/CenterFocusStrong';
import Grid from '@mui/material/Grid';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import ToggleButton from '@mui/material/ToggleButton';
import {
	Chart as ChartJS,
	LinearScale,
	CategoryScale,
	TimeScale,
	BarElement,
	PointElement,
	LineElement,
	Legend,
	Tooltip,
	Filler,
	LineController,
	ScatterController,
} from 'chart.js';
import { Chart } from 'react-chartjs-2';
import zoomPlugin from 'chartjs-plugin-zoom';
import 'chartjs-adapter-date-fns';

// Register Chart.JS Plugins
ChartJS.register(
	LinearScale,
	CategoryScale,
	TimeScale,
	BarElement,
	PointElement,
	LineElement,
	Legend,
	Tooltip,
	Filler,
	LineController,
	ScatterController,
	zoomPlugin,
);

// Custom tooltip positioner that anchors to the first dataset
Tooltip.positioners.firstDataset = function(elements, eventPosition) {
	if (!elements.length) {
		return false;
	}
	
	// Find the element from the dataset index 0 and position the tooltip at it
	const firstDataset = elements.find(el => el.datasetIndex === 0);
	if (firstDataset) {
		return {
			x: firstDataset.element.x,
			y: firstDataset.element.y,
		};
	}
	
	// Fallback to first element if dataset index 0 not found
	return {
		x: elements[0].element.x,
		y: elements[0].element.y,
	};
};


export default function SystemDischargeTrendGraph(props) {
	const { trendData, limitData, graphScalesData, graphType, loading, noCurrentMeasurement } = props;

	const { locationNum } = useParams();
	
	const chartRef = useRef(null);
	
	const [graphData, setGraphData] = useState([]);
	const [graphSettings, setGraphSettings] = useState({
		limitsEnabled: true,
		avgUnitTempEnabled: true,
	});
	const [graphStyle, setGraphStyle] = useState(GRAPH_STYLE.AREA);
	const [graphOptions, setGraphOptions] = useState({
		maintainAspectRatio: false,
		animation: false,
		options: {
			responsive: true,
		},
		transitions: {
			zoom: {
				animation: {
					duration: 0,
				},
			},
		},
		plugins: {
			legend: {
				display: false,
			},
			tooltip: {
				intersect: false,
				mode: 'nearest',
				axis: 'x',
				position: 'firstDataset',
			},
			zoom: {
				pan: {
					enabled: true,
					mode: 'xy',
				},
				limits: {
					x: {
						minRange: 2,
					},
					y: {
						min: 0,
						max: 100,
						minRange: 5,
					}
				},
				zoom: {
					mode: 'y',
					wheel: {
						enabled: true,
						speed: 0.01,
					},
					pinch: {
						enabled: true,
					},
					onZoomStart: (e) => {
						if (typeof e.chart.config?.options?.plugins?.zoom?.zoom === "undefined") {
							return;
						}
						if (e.event.shiftKey === true) {
							e.chart.config.options.plugins.zoom.zoom.mode = "x";
							e.chart.config.options.plugins.zoom.zoom.scaleMode = "x";
							// e.chart.config.options.plugins.zoom.zoom.wheel.speed = 0.01;
						} else {
							e.chart.config.options.plugins.zoom.zoom.mode = "y";
							e.chart.config.options.plugins.zoom.zoom.scaleMode = "y";
							// e.chart.config.options.plugins.zoom.zoom.wheel.speed = 0.01;
						}
					},
				}
			},
		},
	});
	
	// const [isLoading, setIsLoading] = useState(loading);

	// Keep track of the limits data for drawing the limit lines
	const limitDataRef = useRef(null);
	useEffect(() => {
		limitDataRef.current = limitData;
	}, [limitData]);
	
	// Keep track of the graph type for drawing limit lines
	const graphTypeRef = useRef(null);
	useEffect(() => {
		graphTypeRef.current = graphType;
		if (graphType === 0) {
			setGraphStyle(GRAPH_STYLE.LINE);
		}
	}, [graphType]);
	
	// Keep track of the graph settings for drawing limit lines
	const graphSettingsRef = useRef(null);
	useEffect(() => {
		graphSettingsRef.current = graphSettings;
	}, [graphSettings]);

	// Update the graph data sets
	useEffect(() => {
		if (typeof trendData != "undefined" && trendData !== null && !isEmpty(trendData)) {
			const systemData = GetGraphData(trendData.measurements, graphType);
			if (systemData !== undefined && systemData !== null) {
				setGraphData(systemData);
			}
		}
	}, [trendData, graphType, graphSettings, graphStyle, limitData]);

	useEffect(() => {
		const graphScales = SetGraphYScale(graphType);
		if (graphScales !== undefined && graphScales !== null && !isEmpty(trendData)) {

			const firstDate = formatDateTime(trendData.measurements[0].datetime);
			const lastDate = formatDateTime(trendData.measurements[trendData.measurements.length - 1].datetime);

			setGraphOptions((prevState) => {
				return {
					...prevState,
					scales: {
						y: graphScales,
						x: {
							title: {
								display: false,
							},
							suggestedMin: firstDate,
							suggestedMax: lastDate,
						}
					}
				}
			});
		}
	}, [graphScalesData, graphType]);
	
	useEffect(() => {
		SetGraphBounds();
	}, [graphType]);

	const graphPlugins = [
		{
			afterDraw: chart => DrawVerticalDashedLineAtTooltip(chart),
		},
		{
			afterDatasetDraw: chart => {
				let graphType = graphTypeRef.current;
				let graphSettings = graphSettingsRef.current;
				let limitData = limitDataRef.current;

				if (limitData != undefined && limitData != null && !isEmpty(limitData)) {
					// If enabled, draw a horizontal dashed line at the various limit values
					if (graphSettings.limitsEnabled) {
						const limits = GetLimitsData(limitData, graphType);
						if (limits != undefined && limits != null && limits.length > 0) {
							limits.forEach((limit) => {
								DrawHorizontalDashedLine(chart, limit.value, limit.color);
							});
						}
					}
				}
			}
		}
	];
	
	function SetGraphBounds() {
		let graphZoom = GRAPH_BOUNDS.DISCHARGE_CURRENT;
		switch(graphType) {
			case 0:  // Voltage/Current/Power
				graphZoom = trendData?.charge_event ?
					GRAPH_BOUNDS.SYSTEM_VOLTAGE :  // Use voltage bounds during charge (low current) event
					GRAPH_BOUNDS.DISCHARGE_CURRENT;
				break;

			case 1:  // Voltage
				graphZoom = GRAPH_BOUNDS.SYSTEM_VOLTAGE;
				break;

			case 2:  // Current
				graphZoom = GRAPH_BOUNDS.DISCHARGE_CURRENT;
				break;

			case 3:  // Temperature
				graphZoom = {
					...GRAPH_BOUNDS.TEMPERATURE,
					bounds_max: measUnitsConversion(GRAPH_BOUNDS.TEMPERATURE.bounds_max),
					bounds_min: measUnitsConversion(GRAPH_BOUNDS.TEMPERATURE.bounds_min),
				};
				break;

			default:
				graphZoom = GRAPH_BOUNDS.DISCHARGE_CURRENT;
				break;
		}
		
		setGraphOptions((prevState) => {
			return {
				...prevState,
				plugins: {
					...prevState.plugins,
					zoom: {
						...prevState.plugins.zoom,
						limits: {
							...prevState.plugins.zoom.limits,
							y: {
								max: graphZoom.bounds_max,
								min: graphZoom.bounds_min,
								minRange: graphZoom.min_range
							},
						},
					},
				},
			}
		});
	}

	function GetLimitsData(limitData, graphType) {
		let critHiLimit = DATA_ERROR_VALUE.INVALID_VALUE;
		let critLoLimit = DATA_ERROR_VALUE.INVALID_VALUE;
		
		if (limitData != undefined && limitData != null && !isEmpty(limitData) &&
			graphType != undefined && graphType != null) {
			switch(graphType) {
				case 0:
					if (!noCurrentMeasurement) {  // Only set power limits if current measurement is enabled
						critHiLimit = limitData.current.discharge;
					}
					critLoLimit = limitData.volt;
					break;
				case 1:
					critLoLimit = limitData.volt;
					break;
				case 2:
					critHiLimit = limitData.current.discharge;
					break;
				case 3:
					critHiLimit = measUnitsConversion(limitData.temp.hi_crit);
					critLoLimit = measUnitsConversion(limitData.temp.lo_crit);
					break;
				default:
					critHiLimit = DATA_ERROR_VALUE.INVALID_VALUE;
					critLoLimit = DATA_ERROR_VALUE.INVALID_VALUE;
					break;
			}
		}

		// Get the associated limit colors
		let hiCritColor = AnalysisResultToStateColor(ANALYSIS_RESULT.ANALYSIS_ABOVE_LIMITS_CRITICAL);
		let loCritColor = AnalysisResultToStateColor(ANALYSIS_RESULT.ANALYSIS_BELOW_LIMITS_CRITICAL);

		let limitSets = [];
		if (critHiLimit !== undefined && critHiLimit > DATA_ERROR_VALUE.INVALID_VALUE) {
			limitSets.push({
				value: critHiLimit,
				color: hiCritColor
			});
		}

		if (critLoLimit !== undefined && critLoLimit > DATA_ERROR_VALUE.INVALID_VALUE) {
			limitSets.push({
				value: critLoLimit,
				color: loCritColor
			});
		}
		
		return limitSets;
	}


	function SetGraphYScale(graphType) {

		if (trendData !== undefined && trendData !== null && !isEmpty(trendData) &&
			graphScalesData !== undefined && graphScalesData !== null && !isEmpty(graphScalesData)) {

			const scalesData = graphScalesData.system;

			let max = DATA_ERROR_VALUE.INVALID_VALUE;
			let min = DATA_ERROR_VALUE.INVALID_VALUE;
			let title = "";
			
			switch (graphType) {
				case 0:  // Voltage/Current/Power - keep manual computing scale
					let maxCurrent = validValue(trendData.stats?.current?.max, 5000);
					let maxVolt = validValue(trendData.stats?.voltage?.max, 600);
					
					max = Math.max(maxCurrent, maxVolt) * 1.15;
					min = 0;
					title = "Calculated Power (kW)";
					break;

				case 1:  // Voltage
					max = validValue(scalesData.voltage?.max, GRAPH_BOUNDS.SYSTEM_DISCHARGE_VOLTAGE.default_max);
					min = validValue(scalesData.voltage?.min_discharge, GRAPH_BOUNDS.SYSTEM_DISCHARGE_VOLTAGE.default_min);
					title = "System Voltage (VDC)";
					break;

				case 2:  // Current
					max = validValue(scalesData.current?.discharge_max, GRAPH_BOUNDS.DISCHARGE_CURRENT.default_max);
					min = GRAPH_BOUNDS.DISCHARGE_CURRENT.default_min;

					// Adjust max graph scale for charge event
					if (trendData.charge_event) {
						max = validValue(scalesData.current?.charge_max, GRAPH_BOUNDS.CHARGE_CURRENT.default_max);
						min = GRAPH_BOUNDS.CHARGE_CURRENT.default_min;
					}

					title = "System Current (A)";
					break;

				case 3:  // Temperature
					const maxC = validValue(scalesData.temperature?.max, GRAPH_BOUNDS.TEMPERATURE.default_max);
					const minC = validValue(scalesData.temperature?.min, GRAPH_BOUNDS.TEMPERATURE.default_min);
					max = measUnitsConversion(maxC);
					min = measUnitsConversion(minC);
					title = 'Ambient Temperature (' + getUserTempUnits() + ')';
					break;

				default:  // Voltage/Current/Power - keep manual computing scale
					maxCurrent = validValue(trendData.stats?.current.max, 5000);
					maxVolt = validValue(trendData.stats?.voltage?.max, 600);
					
					max = Math.max(maxCurrent, maxVolt) * 1.15;
					min = 0;
					title = "Calculated Power (kW)";
					break;
			}

			if (max !== DATA_ERROR_VALUE.INVALID_VALUE && min !== DATA_ERROR_VALUE.INVALID_VALUE) {
				return {
					max: max,
					min: min,
					title: {
						display: true,
						text: title,
						font: {
							family: "Saira, sans-serif",
							size: 18,
							weight: 600,
						},
					},
				};
			}
		}

		return {};
	}

	function GetMeasurementInfo(measurement, graphType) {
		let value, analysis = 0;
		switch(graphType) {
			case 0:
				value = measurement.power.measurement?.toFixed(3);
				analysis = (measurement.power.analysis_valid) ? 
					measurement.power.analysis_result : 0;
				break;
			case 1:
				value = measurement.voltage.measurement?.toFixed(3);
				analysis = (measurement.voltage.analysis_valid) ? 
					measurement.voltage.analysis_result : 0;
				break;
			case 2:
				value = measurement.current.measurement?.toFixed(3);
				analysis = (measurement.current.analysis_valid) ? 
					measurement.current.analysis_result : 0;
				break;
			case 3:
				let temp = measurement.temperature.measurement?.toFixed(3);
				value = measUnitsConversion(temp);
				analysis = (measurement.temperature.analysis_valid) ? 
					measurement.temperature.analysis_result : 0;
				break;
			default:
				value = measurement.voltage.measurement?.toFixed(3);
				analysis = (measurement.voltage.analysis_valid) ? 
					measurement.voltage.analysis_result : 0;
				break;
		}
		value = (value !== null && value !== undefined && value > DATA_ERROR_VALUE.INVALID_VALUE) ? value : DATA_ERROR_VALUE.INVALID_VALUE;
		let analysisColor = AnalysisResultToStateColor(analysis);

		return {value, analysisColor};
	}

	function GetGraphTypeText(graphType) {
		switch(graphType) {
			case 0:
				return "Calculated Power";
			case 1:
				return "System Voltage";
			case 2:
				return "System Current";
			case 3:
				return "Ambient Temperature";
			default:
				return "Calculated Power";
		}
	}

	function GetAvgUnitTempData(measurementsTrendData, graphStyle) {
		let chartData = [];
		let hasData = false;

		measurementsTrendData.forEach((measurement) => {
			const label = formatDateTime(measurement.datetime);
			const avgUnitTemp = measurement.unit_temperatures?.overall_avg;

			// Only push data points when there's a valid value (matching GetSingleTrendData behavior)
			if (avgUnitTemp !== null && avgUnitTemp !== undefined && avgUnitTemp > DATA_ERROR_VALUE.INVALID_VALUE) {
				const tempConverted = measUnitsConversion(avgUnitTemp)?.toFixed(1);
				chartData.push({x: label, y: tempConverted});
				hasData = true;
			}
		});

		if (!hasData) {
			return null;
		}

		return {
			type: graphStyle === GRAPH_STYLE.POINT ? 'scatter' : 'line',
			label: 'Avg Unit Temperature',
			borderColor: 'rgb(35, 141, 193)',
			backgroundColor: 'rgba(35, 141, 193, 0.7)',
			pointRadius: 3,
			hitRadius: 20,
			borderWidth: 2,
			fill: false,
			spanGaps: true,
			data: chartData,
			order: 100,
		};
	}

	function GetGraphData(measurementsTrendData, graphType) {
		// Pick the units to graph based on the selected string
		if (measurementsTrendData && measurementsTrendData.length > 0) {
			
			// Get the measurement data for the specified measurement data and graph type
			function GetSingleTrendData(measurementData, graphType, graphStyle, colorOverride = null) {
	
				let barColor = [];
				let borderColor = [];
				let chartData = [];
	
				const graphTypeText = GetGraphTypeText(graphType);
				let graphFill = colorOverride !== null ? false : graphStyle === GRAPH_STYLE.AREA;
	
				measurementData.forEach((measurement) => {
					const { value, analysisColor } = GetMeasurementInfo(measurement, graphType);
					
					let setColor = colorOverride !== null ? colorOverride : analysisColor;
					let label = formatDateTime(measurement.datetime);

					if (value !== DATA_ERROR_VALUE.INVALID_VALUE) {
						barColor.push(setColor + "77");
						borderColor.push(setColor);
						chartData.push({x: label, y: value});
					}
				});

				return {
					type: graphStyle === GRAPH_STYLE.POINT ? 'scatter' : 'line',
					label: graphTypeText,
					backgroundColor: barColor,
					borderColor: borderColor,
					segment: {
						borderColor: (ctx) => (barColor[ctx.p1DataIndex]),
						backgroundColor: (ctx) => (barColor[ctx.p1DataIndex]),
					},
					data: chartData,
					fill: graphFill,
					order: 10,
				}
			}
	
			let dataSets = [];
			if (graphType === 0) {
				if (!noCurrentMeasurement) {  // Include Power only if current is not disabled (cannot calculate power with no current)
					dataSets.push(GetSingleTrendData(measurementsTrendData, 0, graphStyle, STATE_COLOR.WITHIN_LIMITS));
				}

				dataSets.push(GetSingleTrendData(measurementsTrendData, 1, graphStyle, STATE_COLOR.MAINTENANCE));

				if (!noCurrentMeasurement) {  // Only include Current if not disabled
					dataSets.push(GetSingleTrendData(measurementsTrendData, 2, graphStyle, STATE_COLOR.CRITICAL));
				}
			} else {
				dataSets.push(GetSingleTrendData(measurementsTrendData, graphType, graphStyle));
			}

			// Add average unit temperature dataset if enabled and on temperature graph
			if (graphType === 3 && graphSettings.avgUnitTempEnabled) {
				const avgUnitTempData = GetAvgUnitTempData(measurementsTrendData, graphStyle);
				if (avgUnitTempData) {
					dataSets.push(avgUnitTempData);
				}
			}
	
			return {
				labels: measurementsTrendData.map((measurement) => formatDateTime(measurement.datetime)),
				datasets: dataSets,
			}
		}
	
		return null;
	}

	const handleLimitMarkersToggle = () => {
		setGraphSettings((prevState) => ({
			...prevState,
			limitsEnabled: !graphSettings.limitsEnabled})
		);
	};

	const handleAvgUnitTempToggle = () => {
		setGraphSettings((prevState) => ({
			...prevState,
			avgUnitTempEnabled: !graphSettings.avgUnitTempEnabled})
		);
	};

	const handleGraphResetZoom = () => {
		chartRef.current.resetZoom();
	};

	const handleGraphStyle = (event, newStyle) => {
		if (newStyle !== null) {
			setGraphStyle(newStyle);
		}
	};

	return (
		<div
			style={{
				width: '100%',
				height: '99%',
				display: 'flex',
				flexDirection: 'column',
				position: 'relative',
			}}
		>
			<div style={{ height: '100%', width: '100%' }}>
				{ (graphData !== undefined && graphData.length !== 0) &&
					<Chart
						ref={chartRef}
						type='line'
						options={graphOptions}
						data={graphData}
						plugins = {graphPlugins}
					/>
				}
			</div>
			<Grid container spacing={1}>
				<Grid item xs alignItems="left">
					<ToggleButtonGroup
						value={[]}
						onChange={() => {}}
						aria-label="graph markers shown"
						size='small'
						sx={{ padding: '6px' }}
					>
						<ToggleButton
							value="limitMarkers"
							selected={graphSettings.limitsEnabled}
							onClick={handleLimitMarkersToggle}
						>
							Limits
						</ToggleButton>
						{graphType === 3 && (
							<ToggleButton
								value="avgUnitTemp"
								selected={graphSettings.avgUnitTempEnabled}
								onClick={handleAvgUnitTempToggle}
							>
								Avg Unit Temp
							</ToggleButton>
						)}
					</ToggleButtonGroup>
				</Grid>
				<Grid item xs alignItems="center">
					<Button
						variant="contained"
						startIcon={<CenterFocusStrongIcon />}
						onClick={handleGraphResetZoom}
						sx={{
							marginY: 1,
							backgroundColor: 'white',
							border: '1px solid lightgray',
							color: 'black',
							":hover": {
								backgroundColor: 'lightgray',
								boxShadow: 'none',
							}

						}}
					>
						Reset View
					</Button>
				</Grid>
				<Grid item>
					<ToggleButtonGroup
						value={graphStyle}
						onChange={handleGraphStyle}
						exclusive
						aria-label="graph marker type"
						size='small'
						sx={{ padding: '6px' }}
					>
						<ToggleButton
							value={GRAPH_STYLE.AREA}
							aria-label="Area Graph Style"
							disabled={graphType === 0}
						>
							Area
						</ToggleButton>
						<ToggleButton
							value={GRAPH_STYLE.LINE}
							aria-label="Line Graph Style"
						>
							Line
						</ToggleButton>
						<ToggleButton
							value={GRAPH_STYLE.POINT}
							aria-label="Point Graph Style"
						>
							Point
						</ToggleButton>
					</ToggleButtonGroup>
				</Grid>
			</Grid>
		</div>
	);
}